<template>
  <v-container
    id="property-settings-list"
    :key="containerKey"
  >
    <valkyrie-list
      class="admin-list"
      show-select
      has-filters
      :list-data="listData"
      :pagination="listPagination"
      list-name="editPropertyPermissions"
      :headers="headers"
      :is-loading="loading"
      :item-key="'id'"
      :selected-rows="selected"
      @check-item="handleSelectedRows"
      @update-pagination="handleUpdatePagination"
    >
      <template v-slot:list-header>
        <property-settings-list-header
          :searchable="hasDataToQuery"
          :filterable="hasDataToQuery"
          @clear-filters="handleClearFilters"
          @search="handleUpdateSearch"
        >
          <template>
            <div class="my-auto">
              <v-btn
                class="ma-1 icon-btn"
                elevation="1"
                color="default"
                width="140px"
                @click="handleCancel"
              >
                Cancel
              </v-btn>
              <v-btn
                class="ma-1 icon-btn"
                elevation="1"
                color="primary"
                width="140px"
                :disabled="saveDisabled"
                @click="handleSave"
              >
                Save
              </v-btn>
            </div>
          </template>
          <template #tags>
            <async-multiselect-filter
              filter-key="tag_id"
              :searchable="false"
              :fetch-function="fetchAllPropertyTags"
              @apply-multiselect="handleUpdateFilters"
            />
          </template>
        </property-settings-list-header>
      </template>
      <!-- eslint-disable vue/valid-v-slot -->
      <template #item.name="{item}">
        <router-link
          :to="{ name: 'Property Details', params: {id: `${item.id}` } }"
          class="property-name"
        >
          {{ item.name }}
        </router-link>
      </template>
      <template #item.tags="{item}">
        <v-chip
          v-for="(tag, idx) in item.tags"
          :key="idx"
          outlined
          color="#535353"
          class="ma-1 tag"
        >
          {{ tag }}
        </v-chip>
      </template>
      <template #item.createdAt="{item}">
        {{ formatDate(item.createdAt, 'date') }}
        <span class="font-weight-bold">{{ formatDate(item.createdAt, 'time') }}</span>
        {{ ' ' }}
        <span
          v-if="isValidDate(item.createdAt)"
          class="grey--text lighten-4"
        >
          {{ item.createdAt | formatTimezone }}
        </span>
      </template>
      <template slot="empty-state">
        <empty-state
          icon="$faHomeLight"
          text="No properties found"
        />
      </template>
    </valkyrie-list>
  </v-container>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { DateTime } from 'luxon'
  import listMixin from '@/helpers/list-mixin'
  import listControllerMixin from '@/helpers/list-controller-mixin'
  import ValkyrieList from '@/components/ValkyrieList'
  import EmptyState from '@/components/base/EmptyState'
  import PropertySettingsListHeader from './PropertySettingsListHeader.vue'
  import AsyncMultiselectFilter from '@/components/filters/AsyncMultiselectFilter.vue'

  export default {
    name: 'EditPropertyPermissionsList',
    filters: {
      formatTimezone (val) {
        return DateTime.fromISO(val).toFormat('(ZZZZ)')
      },
    },
    components: { ValkyrieList, PropertySettingsListHeader, EmptyState, AsyncMultiselectFilter },
    mixins: [listMixin, listControllerMixin],
    data () {
      return {
        hasDataToQuery: false,
        containerKey: 0,
        selected: [],
      }
    },
    computed: {
      ...mapGetters({
        selectedOrg: 'Permissions/currentSelectedOrg',
        total: 'Users/userPropertyPermissionsTotal',
      }),
      headers () {
        return [
          {
            text: 'Name',
            value: 'name',
          },
          {
            text: 'Address',
            value: 'street1',
          },
          {
            text: 'City',
            value: 'city',
          },
          {
            text: 'State',
            value: 'region',
          },
          {
            text: 'Tags',
            value: 'tags',
          },
          {
            text: 'Property Added',
            value: 'createdAt',
          },
        ]
      },
      orgId () {
        return this.selectedOrg.id
      },
      userId () {
        return this.$route.params.id
      },
      initialSelectedRows () {
        return this.listData.reduce((acc, row) => row.canAccess ? [row, ...acc] : acc, [])
      },
      saveDisabled () {
        return JSON.stringify(this.initialSelectedRows) === JSON.stringify(this.selected)
      },
      filterPayload () {
        return {
          pagination: { perPage: 1000, pageNum: 1 },
          ...(this.orgId ? { filter: [{ key: 'organization_id', operator: '=', value: `${this.orgId}` }] } : {}),
        }
      },
      staticFilters () {
        if (this.orgId) return [{ key: 'organization_id', operator: '=', value: `${this.orgId}` }]
        return []
      },
      listPagination () {
        return { ...this.pagination, total: this.total }
      },
    },
    watch: {
      $route (to) {
        if (to.query.index) {
          this.initializeList()
          this.containerKey++
        }
      },
    },
    mounted () {
      this.initializeList()
    },
    methods: {
      fetchList () {
        this.loading = true
        return this.$store.dispatch('Users/fetchEditPropertyPermissionsList', { userId: this.userId, listPayload: this.listPayload })
          .then((res) => {
            this.listData = res.data.data
            return res
          })
          .finally(() => { this.loading = false })
      },
      updateTotal (total) {
        this.$store.commit('Users/updateUserPropertyPermissionsTotal', total)
      },
      initializeList () {
        this.fetchList()
          .then((res) => {
            this.updateTotal(res.data.pagination.total)
            this.hasDataToQuery = res.data.pagination.total > 0
          })
          .then(() => this.initializeSelectedRows())
      },
      handleSave () {
        const newIds = this.selected.map((p) => p.id)
        const oldIds = this.initialSelectedRows.map((p) => p.id)
        const removedAccess = oldIds.reduce((acc, rowId) => newIds.includes(rowId) ? acc : [rowId, ...acc], [])
        const addedAccess = newIds.reduce((acc, rowId) => oldIds.includes(rowId) ? acc : [rowId, ...acc], [])
        const actions = []
        if (addedAccess.length) actions.push(this.$store.dispatch('Users/updateUserPropertyAccess', { userId: this.userId, body: { properties: addedAccess } }))
        if (removedAccess.length) actions.push(this.$store.dispatch('Users/deleteUserPropertyAccess', { userId: this.userId, body: { properties: removedAccess } }))
        Promise.all(actions).then(() => { this.$emit('save-success') })
      },
      handleCancel () {
        this.updatePagination({ pageNum: 1 })
        this.$emit('cancel')
      },
      handleSelectedRows (rows) {
        this.selected = rows
      },
      initializeSelectedRows () {
        this.selected = this.initialSelectedRows
      },
      fetchAllPropertyTags (payload) {
        return this.$store.dispatch('Properties/fetchPropertyListFilters', { key: 'tags', ...this.filterPayload, ...payload })
      },
    },
  }
</script>

<style lang="scss">
#property-settings-list {
  .property-name {
    color: #4C6489;
    font-weight: 600;
    letter-spacing: 0.191002px;
    text-decoration: none;
  }
  .tag {
    border-color: #808182;
    .v-chip__content {
      font-size: 14px;
    }
  }
.admin-list tr:not(.v-data-table__selected) {
    & td:nth-child(n+3) {
      color: #E1EBEE !important;
    }
    .tag {
      border-color: #E1EBEE !important;
      color: #E1EBEE !important;
    }
  }
}
</style>
